import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Tooltip, IconButton, Typography, Box } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import DateRangeFilter from "../components/common/DateRangeFilter";
import StandardTable, { TableColumn } from "../components/common/StandardTable";
import AppButton from "../components/buttons/AppButton";
import CreateInvoiceModal from "../components/invoices/CreateInvoiceModal";

import useDebounce from "../hooks/useDebounce";
import useWorkspaceNavigation from "../hooks/useWorkspaceNavigation";
import { usePermissions } from "../hooks/usePermissions";
import { getInvoices } from "../redux/actions/invoiceActions";
import StatusChip from "../components/common/StatusChip";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { InvoiceSummaryDto } from "../shared/dtos/invoice/InvoiceSummaryDto";
import { formatPlainNumber } from "../utils/appUtils";

const InvoicesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { navigateToTab } = useWorkspaceNavigation();
  const { hasPermission } = usePermissions();

  const today = new Date().toISOString().split("T")[0];

  const {
    items: invoices,
    pagination,
    loadingFetchAll: loadingInvoices,
    errorFetchAll: errorLoadingInvoices,
    errorCreate: errorCreateInvoice,
  } = useAppSelector((state) => state.invoice);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);
  const [startDate, setStartDate] = useState<string>(today);
  const [endDate, setEndDate] = useState<string>(today);
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });

  const [isCreateOpen, setIsCreateOpen] = useState<boolean>(false);

  const canRead = hasPermission("invoice:read_all");
  const canCreate = hasPermission("invoice:create");

  const fetchInvoicesList = useCallback(() => {
    if (!canRead) return;
    dispatch(
      getInvoices({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch,
        startDate,
        endDate,
      }),
    );
  }, [dispatch, pageConfig, debouncedSearch, startDate, endDate, canRead]);

  useEffect(() => {
    fetchInvoicesList();
  }, [fetchInvoicesList]);

  useEffect(() => {
    setPageConfig((p) => ({ ...p, page: 1 }));
  }, [debouncedSearch, startDate, endDate]);

  const columns: TableColumn<InvoiceSummaryDto>[] = useMemo(
    () => [
      // --- DEFAULT VISIBLE COLUMNS ---
      {
        id: "invoiceNumber",
        label: "INV #",
        minWidth: 100,
        format: (_, row) => (
          <Box component="b" sx={{ color: "#c77dff" }}>
            {row.invoiceNumber}
          </Box>
        ),
      },
      {
        id: "issueDate",
        label: "Issue Date",
        minWidth: 100,
        format: (_, row) => new Date(row.issueDate).toLocaleDateString(),
      },
      { id: "customerName", label: "Customer", minWidth: 150 },
      {
        id: "status",
        label: "Status",
        minWidth: 110,
        format: (_, row) => <StatusChip status={row.status} />,
      },
      { id: "currencyCode", label: "CURRENCY", minWidth: 60, align: "center" },
      {
        id: "total",
        label: "Total",
        minWidth: 100,
        align: "right",
        format: (_, row) => (
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {formatPlainNumber(row.total)}
          </Typography>
        ),
      },
      {
        id: "amountPaid",
        label: "Paid",
        minWidth: 100,
        align: "right",
        format: (_, row) => (
          <Typography
            variant="body2"
            sx={{ fontWeight: "bold", color: "success.main" }}
          >
            {formatPlainNumber(row.amountPaid)}
          </Typography>
        ),
      },
      {
        id: "id", // Reusing ID as dummy key for calculated column
        label: "Due",
        minWidth: 100,
        align: "right",
        format: (_, row) => {
          const due = (row.total || 0) - (row.amountPaid || 0);
          return (
            <Typography
              variant="body2"
              sx={{
                fontWeight: "bold",
                color: due > 0.01 ? "error.main" : "text.secondary",
              }}
            >
              {formatPlainNumber(due)}
            </Typography>
          );
        },
      },

      // --- HIDDEN BY DEFAULT ---
      {
        id: "dueDate",
        label: "Due Date",
        minWidth: 100,
        defaultHidden: true,
        format: (_, row) => new Date(row.dueDate).toLocaleDateString(),
      },
      {
        id: "saleType",
        label: "Sale Type",
        minWidth: 100,
        defaultHidden: true,
      },
      {
        id: "transactionStatus",
        label: "Txn Status",
        minWidth: 110,
        defaultHidden: true,
        format: (_, row) => <StatusChip status={row.transactionStatus} />,
      },
      {
        id: "exchangeRate",
        label: "Rate",
        minWidth: 80,
        defaultHidden: true,
        align: "right",
        format: (_, row) => formatPlainNumber(row.exchangeRate),
      },
      {
        id: "subtotal",
        label: "Subtotal",
        minWidth: 100,
        defaultHidden: true,
        align: "right",
        format: (_, row) => formatPlainNumber(row.subtotal),
      },
      {
        id: "totalDiscount",
        label: "Discount",
        minWidth: 100,
        defaultHidden: true,
        align: "right",
        format: (_, row) => formatPlainNumber(row.totalDiscount),
      },
      {
        id: "totalTaxableAmount",
        label: "Taxable",
        minWidth: 100,
        defaultHidden: true,
        align: "right",
        format: (_, row) => formatPlainNumber(row.totalTaxableAmount),
      },
      {
        id: "totalTax",
        label: "Tax",
        minWidth: 100,
        defaultHidden: true,
        align: "right",
        format: (_, row) => formatPlainNumber(row.totalTax),
      },
    ],
    [],
  );

  return (
    <PageContainer
      title="Invoices"
      subtitle="Manage customer billing, track payments, and follow up on overdue accounts."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setIsCreateOpen(true)}
          >
            Create Invoice
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search INV # or Customer..."
      >
        <DateRangeFilter
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </PageToolbar>
      <StandardTable
        columns={columns}
        data={invoices}
        loading={loadingInvoices}
        error={errorLoadingInvoices}
        onRetry={fetchInvoicesList}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(p) => setPageConfig((c) => ({ ...c, page: p }))}
        onRowsPerPageChange={(l) =>
          setPageConfig((c) => ({ ...c, limit: l, page: 1 }))
        }
        enableColumnManagement={true}
        rowAction={(row) => (
          <Tooltip title="View Invoice">
            <IconButton
              size="small"
              color="primary"
              onClick={() => navigateToTab(`/invoices/${row.id}`)}
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      />
      {isCreateOpen && (
        <CreateInvoiceModal
          open={isCreateOpen}
          onClose={() => {
            setIsCreateOpen(false);
            fetchInvoicesList();
          }}
          error={errorCreateInvoice}
        />
      )}
    </PageContainer>
  );
};
export default InvoicesPage;
